import type { BackendTypes } from '@tf/api';
import { Stack, TFText } from '@tf/ui';
import { AccountTasks } from '@/components/accounts';
import type { ModalProps } from '@/components/accounts/AccountToolbar/AccountStatus/types/types';
import type { PrimitiveField } from '@/components/shared';
import { FieldName } from '@/components/shared';
import { getLabel } from '@/core/utils';

const getFields = (name: string): Record<string, PrimitiveField> => {
	return {
		onboardingDate: {
			name: 'onboarding_date',
			component: FieldName.DateField,
			label: 'Onboarding date',
		},
		closingDate: {
			name: 'closing_date',
			component: FieldName.DateField,
			label: 'Closing date',
		},
		accountName: {
			name: 'account_name',
			component: FieldName.TextField,
			label: `Enter the account name “${name}” to continue`,
		},
	};
};

export const getModalData = ({
	accountStatus,
	newAccountStatus,
	verificationStatus,
	accountName,
}: {
	accountStatus: BackendTypes.OverviewAccountStatus | 'UNDEFINED';
	newAccountStatus: BackendTypes.OverviewAccountStatus;
	verificationStatus?: BackendTypes.ReviewState;
	accountName: string;
}): ModalProps => {
	const fields = getFields(accountName);

	if (accountStatus === 'PROSPECT' && newAccountStatus === 'ACTIVE' && verificationStatus === 'VERIFIED') {
		return {
			title: "Please confirm you'd like to activate this account?",
			content: (
				<Stack gap="0.75rem">
					<TFText>The account review is completed.</TFText>
					<TFText>Once activated, the profile will be monitored against a real-time AML risk database.</TFText>
				</Stack>
			),
			// @ts-expect-error FIXME TS error is suppressed for migration, fix it later
			additionalFields: [fields.onboardingDate],
			confirmButtonLabel: 'Activate account',
		};
	}

	if (accountStatus === 'PROSPECT' && newAccountStatus === 'ACTIVE') {
		return {
			title: "Confirm you'd like to activate this account?",
			content: (
				<Stack gap="md">
					<TFText>
						Please note the account review is incomplete and requires further info in the following forms:
					</TFText>
					<AccountTasks hideLabel />
					<TFText>Once activated, the profile will be monitored against a real-time AML risk database.</TFText>
				</Stack>
			),
			// @ts-expect-error FIXME TS error is suppressed for migration, fix it later
			additionalFields: [fields.onboardingDate],
			confirmButtonLabel: 'Activate',
		};
	}

	if (accountStatus === 'PROSPECT' && newAccountStatus === 'REJECTED') {
		return {
			title: "Please confirm you'd like to reject this application?",
			text: 'Once rejected, the account cannot be activated.',
			confirmButtonLabel: 'Reject account',
		};
	}

	if (accountStatus === 'ACTIVE' && newAccountStatus === 'SUSPENDED') {
		return {
			title: "Confirm you'd like to suspend this account?",
			text: 'Please note, once suspended, you will always be able to reactivate the account and the profile will continue to be monitored. If you prefer to permanently deactivate this account, please use the "Close" option instead.',
			confirmButtonLabel: 'Suspend account',
		};
	}

	if (accountStatus === 'SUSPENDED' && newAccountStatus === 'ACTIVE') {
		return {
			title: "Please confirm you'd like to reactivate this account?",
			text: 'Once activated, the profile will be monitored against an AML risk database.',
			confirmButtonLabel: 'Reactivate account',
		};
	}

	if ((accountStatus === 'ACTIVE' || accountStatus === 'SUSPENDED') && newAccountStatus === 'CLOSED') {
		return {
			title: "Please confirm you'd like to close this account?",
			text: 'Once the account is closed, it will no longer be monitored',
			// @ts-expect-error FIXME TS error is suppressed for migration, fix it later
			additionalFields: [fields.closingDate],
			confirmButtonLabel: 'Close account',
		};
	}

	if (newAccountStatus === 'ARCHIVED') {
		return {
			title: `Archive ${accountName}?`,
			text: 'Once the account is archived, it will no longer be monitored',
			confirmButtonLabel: 'Archive account',
			// @ts-expect-error FIXME TS error is suppressed for migration, fix it later
			additionalFields: [fields.accountName],
			confirmButtonColor: 'red',
			accountNameConfirmation: accountName,
		};
	}

	return {
		title: 'Please confirm status change',
		text: `Change account status to ${getLabel(newAccountStatus, { kind: 'accountStatus' })}?`,
		confirmButtonLabel: 'Confirm',
	};
};
